import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";

type Props = {
  length: number;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  prefix?: ReactNode;
  autoComplete?: string;
  autoFocus?: boolean;
  ariaLabel?: string;
};

/** 整体外框输入框；内部数字按位间距展示（金融数字体） */
export function DigitSplitInput(props: Props) {
  const {
    length,
    value,
    onChange,
    className,
    placeholder,
    prefix,
    autoComplete,
    autoFocus,
    ariaLabel,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  const sanitized = value.replace(/\D/g, "").slice(0, length);
  const showPlaceholder = sanitized.length === 0 && !focused && !!placeholder;
  const showCaret = focused;

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value.replace(/\D/g, "").slice(0, length));
    },
    [length, onChange],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Backspace" || sanitized.length === 0) {
        return;
      }
      e.preventDefault();
      onChange(sanitized.slice(0, -1));
    },
    [onChange, sanitized],
  );

  return (
    <div
      className={[
        "login-digit-input",
        focused ? "login-digit-input--focused" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={focusInput}
    >
      {prefix ? <span className="login-digit-input-prefix">{prefix}</span> : null}
      <input
        ref={inputRef}
        className="login-digit-input-hidden"
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        value={sanitized}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        maxLength={length}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
      />
      <div className="login-digit-input-display" aria-hidden>
        {showPlaceholder ? (
          <span className="login-digit-input-placeholder">{placeholder}</span>
        ) : (
          <span className="login-digit-input-slots">
            {sanitized.split("").map((digit, index) => (
              <span key={index} className="login-digit-input-slot">
                {digit}
              </span>
            ))}
            {showCaret ? (
              <span className="login-digit-input-slot login-digit-input-slot--caret-only">
                <span className="login-digit-input-caret" />
              </span>
            ) : null}
          </span>
        )}
      </div>
    </div>
  );
}
